// --- Utility: Load Image ---
export const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    if (!src) return reject(new Error("No source provided"));
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load: ${src}`));
    img.src = src.startsWith('http') || src.startsWith('blob:') || src.startsWith('data:') 
      ? src 
      : (src.startsWith('/') ? src : `/${src}`);
  });
};

// --- Utility: Get Nested Object Value ---
export const getNestedValue = (obj, path) => {
  if (!path) return undefined;
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

// --- Utility: Safely Evaluate Conditions ---
export const checkCondition = (conditionStr, state) => {
  if (!conditionStr) return true;
  try {
    const evaluator = new Function('state', `with(state) { return ${conditionStr}; }`);
    return evaluator(state);
  } catch (e) {
    console.warn(`Condition evaluation failed for: ${conditionStr}`, e);
    return false;
  }
};

// --- Utility: Composite Emblem Blending ---
export const applyEmblemBlending = (ctx, imgEmblem, config) => {
  if (!config) return;
  
  const { x, y, width: w, height: h, baseColor, highlightColor, blend1, blend2, opacity1, opacity2, renderMode } = config;

  if (renderMode === "simpleMultiply") {
    ctx.globalAlpha = opacity1 || 1;
    ctx.globalCompositeOperation = "multiply";
    ctx.drawImage(imgEmblem, x, y, w, h);
    
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
    return;
  }

  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = w; 
  tempCanvas.height = h;
  const tCtx = tempCanvas.getContext("2d");

  // Phase 1: Base Burn
  tCtx.fillStyle = baseColor;
  tCtx.fillRect(0, 0, w, h);
  tCtx.globalCompositeOperation = "destination-in";
  tCtx.drawImage(imgEmblem, 0, 0, w, h);
  
  ctx.globalAlpha = opacity1;
  ctx.globalCompositeOperation = blend1;
  ctx.drawImage(tempCanvas, x, y, w, h);
  ctx.globalCompositeOperation = "multiply";
  ctx.drawImage(imgEmblem, x, y, w, h);
  ctx.globalAlpha = 1;

  // Phase 2: Highlight Dodge
  tCtx.globalCompositeOperation = "source-over";
  tCtx.fillStyle = highlightColor;
  tCtx.fillRect(0, 0, w, h);
  tCtx.globalCompositeOperation = "destination-in";
  tCtx.drawImage(imgEmblem, 0, 0, w, h);

  ctx.globalAlpha = opacity2;
  ctx.globalCompositeOperation = "multiply";
  ctx.drawImage(imgEmblem, x, y, w, h);
  ctx.globalCompositeOperation = blend2;
  ctx.drawImage(tempCanvas, x, y, w, h);
  
  ctx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 1;
};

// --- Utility: Hearthstone Multi-line Text Renderer ---
// --- Utility: Hearthstone Multi-line Text Renderer ---
export const renderHearthstoneText = (ctxTemp, ctxFinal, txt, params, descriptionBox) => {
  // Clear before the early return so removing a description cannot leave stale text behind.
  ctxTemp.clearRect(0, 0, ctxTemp.canvas.width, ctxTemp.canvas.height);
  ctxFinal.clearRect(0, 0, ctxFinal.canvas.width, ctxFinal.canvas.height);

  if (!descriptionBox) return { activeFontSize: 40, defaultFontSize: 40 };
  params.debug = false;

  if (!txt) return;

  // --- Extract Dynamic Parameters ---
  const { 
      font = "FranklinGothicHS", 
      color = [30, 23, 16], 
      defaultFontSize = 40,
      minFontSize = 20,
      charSize = 5.0,
      lineSpacing = 0.77,
      pixelsPerUnit = 265,
      widthUnity = 1.95,
      heightUnity = 0.87,
      useUnderwear = true,
      isFlipped = false,
      underwearWidthPercent = 0.20,
      underwearHeightPercent = 0.17,
      
      // Resizing behavior options:
      resizeEnabled = true,
      resizeToFitAndGrow = false,         
      resizeToGrowStep = 0.025,            
      resizeShrinkStep = 0.025,            
      
      dropX = 142,
      dropY = 769,
      
      singleLineAdjustment = 0.0,         
      lineSpaceModifier = 1.03,            
      resizeToFitWidthModifier = 0.99     
  } = descriptionBox;

  // --- Apply Markdown & Keywords ---
  txt = txt.replace(/\*\*\*([.\s\S]*?)\*\*\*/ig, "[b][i]$1[/i][/b]");
  txt = txt.replace(/\*\*([.\s\S]*?)\*\*/ig, "[b]$1[/b]");

  txt = txt.replace(/Colossal \+\d+/g, '[b]$&[/b]');
  txt = txt.replace(/Infuse \(\d+\)\:/g, '[b]$&[/b]');
  txt = txt.replace(/Manathirst \(\d+\)\:/g, '[b]$&[/b]');
  txt = txt.replace(/Spell Damage \+\d+/g, "[b]$&[/b]");
  txt = txt.replace(/Activate \(\d+\)\:/g, '[b]$&[/b]');

  const HSTouristClasses = ['Demon Hunter', 'Druid', 'Hunter', 'Mage', 'Paladin', 'Priest', 'Rogue', 'Shaman', 'Warlock', 'Warrior', 'Death Knight'];
  HSTouristClasses.forEach(cls => {
      const regex = new RegExp(`${cls} Tourist`, 'g');
      txt = txt.replace(regex, `[b]$&[/b]`);
  });

  const ingameKeywords = [
      "Battlecry and Deathrattle:", "Battlecry:", "Battlecry", "Deathrattle:", "Deathrattle",
      "Hero Power:", "Inspire:", "Choose One -", "Combo:", "Enrage:", "Taunt", "Charge",
      "Divine Shield", "Mega-Windfury", "Windfury", "Overload:", "Overload",
      "Stealth", "Secret:", "Secret", "Discover", "Immune", "Freeze", "Legendary",
      "Jade Golem", "Frozen", "Silence", "Adapt", "Quest:", "Reward:", "Poisonous",
      "Lifesteal", "Lich King", "Overloaded", "Recruit", "Echo", "Rush", "Start of Game:",
      "Magnetic", "Casts When Drawn", "Overkill:", "Overkill", "Twinspell", "Lackey",
      "Lackeys", "Reborn", "Spell Damage", "Invoke", "Sidequest:", "Start of Combat:",
      "Outcast:", "Outcast", "Dormant", "Spellburst:", "Spellburst", "Corrupt:", "Corrupt",
      "Corrupted", "Frenzy:", "Frenzy", "Tradeable", "Trade", "Questline:", "Honorable Kill:",
      "Colossal", "Dredge", "Infuse", "Spellcraft", "Refresh", "Manathirst", "Corpses",
      "Corpse", "Finale:", "Finale", "Overheal:", "Overheal", "Venomous", "Forge:",
      "Forged", "Titan", "Excavate", "Quickdraw:", "Magnetize", "Magnetized", "Elusive",
      "Miniaturize", "Gigantify", "Gigantic", "Starship Piece", "Starship", "bonus effect",
      "Temporary", "Bonus Effect", "Imbue", "Dark Gift", "Kindred:", "Kindred", "Rewind",
      "Fabled", "Herald", "Shatter", "Prepare"
  ];

const customKeywords = String(params?.customAutobold || '')
      .split(/[\n,]/)
      .map(keyword => keyword.trim())
      .filter(Boolean);
      
  const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // 1. Map static keywords to escaped regex patterns
  const staticKwPatterns = [...new Set(ingameKeywords)]
      .map(escapeRegExp);

  // 2. Map custom keywords to escaped regex patterns (with wildcard replacement)
  const customKwPatterns = [...new Set(customKeywords)]
      .map(keyword => escapeRegExp(keyword).replace(/\\\*/g, '[\\p{L}\\p{N}_-]+'));

  // 3. Combine both lists and sort by length descending to prevent preemption
  const kwPattern = [...staticKwPatterns, ...customKwPatterns]
      .sort((a, b) => b.length - a.length)
      .join('|');

  const staticKwRegex = new RegExp(`(?<!\\p{L})(${kwPattern})(?!\\p{L})`, 'gu');
  txt = txt.replace(staticKwRegex, '[b]$1[/b]');

  // Clean False Positives
  txt = txt.replace(/Silver Hand \[b\]Recruit\[\/b\]/g, 'Silver Hand Recruit');
  if (typeof params !== 'undefined' && params.Type && !params.Type.startsWith('BG')) {
      txt = txt.replace(/\[b\]Refresh\[\/b\]/g, 'Refresh');
      txt = txt.replace(/\[b\]Refreshed\[\/b\]/g, 'Refreshed');
  }

  // Flatten Tags
  function flattenTags(text, tag) {
      let depth = 0;
      const regex = new RegExp(`(\\[${tag}\\]|<${tag}>|\\[\\/${tag}\\]|<\\/${tag}>)`, 'gi');
      let result = '';
      let lastIndex = 0;
      let match;
      while ((match = regex.exec(text)) !== null) {
          result += text.slice(lastIndex, match.index);
          const matchStr = match[1].toLowerCase();
          const isClosing = matchStr.includes(`/${tag}`);
          if (!isClosing) {
              if (depth === 0) result += `[${tag}]`;
              depth++;
          } else {
              depth--;
              if (depth === 0) result += `[/${tag}]`;
              if (depth < 0) depth = 0; 
          }
          lastIndex = regex.lastIndex;
      }
      result += text.slice(lastIndex);
      return result;
  }
  txt = flattenTags(txt, 'b');
  txt = flattenTags(txt, 'i');

  ctxTemp.textBaseline = 'alphabetic';

  // --- Constants and Sizing ---
  const initialFontSize = defaultFontSize + (params?.Overwrite?.FontSize || 0);
  const W = widthUnity * pixelsPerUnit;
  const originalH = heightUnity * pixelsPerUnit;

  let heightPenalty = 0;
  if (params && params.Tribe && (params.Type?.includes('minion') || params.Type === "weapon")) {
      heightPenalty = params.Tribe.includes('/') ? 48 : 0;
  }
  const H = originalH - heightPenalty;

  const uw = W * underwearWidthPercent * 0.5;
  // Keep the debug visualization aligned with the cutout used during layout.
  const targetUnderwearHeight = H * underwearHeightPercent * 0.75;
  const narrowWidth = Math.round(W * (1.0 - underwearWidthPercent));

  // --- English Boundary Conditions ---
  function canWrapBetween(lastChar, wideChar, nextChar) {
      const lastCode = lastChar.charCodeAt(0);
      const wideCode = wideChar.charCodeAt(0);

      const isWideSpace = /\s/.test(wideChar);
      const isLastSpace = /\s/.test(lastChar);

      if (lastCode === 45) { // '-'
          return wideCode < 48 || wideCode > 57;
      }
      if (lastCode === 59) return true; // ';'
      if (wideCode === 124) return true; // '|'

      if (isLastSpace) return false;
      if (isWideSpace) return true;

      const noWrapBefore = [33, 37, 41, 44, 46, 58, 59, 63, 93, 125, 8217, 8221, 8226];
      if (noWrapBefore.includes(wideCode)) return false;

      const noWrapAfter = [36, 40, 91, 123, 8216, 8220];
      if (noWrapAfter.includes(lastCode)) return false;

      return false;
  }

  function breakStringIntoWords(text) {
      const outWordList = [];
      let wordBuilder = "";
      let noWrap = false;

      if (!text) return [];

      let i = 0;
      while (i < text.length) {
          const c2 = text[i];

          if (c2 === '\n') {
              if (wordBuilder.length > 0) {
                  outWordList.push(wordBuilder);
                  wordBuilder = "";
              }
              outWordList.push('\n');
              i++;
              continue;
          }

          if (c2 === '[' && i + 2 < text.length) {
              const next1 = text[i+1];
              const next2 = text[i+2];
              if ((next1 === 'b' || next1 === 'i' || next1 === 'd') && next2 === ']') {
                  if (wordBuilder.length > 0) {
                      outWordList.push(wordBuilder);
                      wordBuilder = "";
                  }
                  outWordList.push(`[${next1}]`);
                  i += 3;
                  continue;
              } else if (next1 === '/' && (next2 === 'b' || next2 === 'i') && text[i+3] === ']') {
                  if (wordBuilder.length > 0) {
                      outWordList.push(wordBuilder);
                      wordBuilder = "";
                  }
                  outWordList.push(`[/${next2}]`);
                  i += 4;
                  continue;
              } else if (next1 === 'x' && next2 === ']') {
                  noWrap = true;
                  if (wordBuilder.length > 0) {
                      outWordList.push(wordBuilder);
                      wordBuilder = "";
                  }
                  outWordList.push(`[x]`);
                  i += 3;
                  continue;
              } else if (next1 === '/' && next2 === 'x' && text[i+3] === ']') {
                  noWrap = false;
                  if (wordBuilder.length > 0) {
                      outWordList.push(wordBuilder);
                      wordBuilder = "";
                  }
                  outWordList.push(`[/x]`);
                  i += 4;
                  continue;
              }
          }

          if (i > 0) {
              const lastChar = text[i - 1];
              const wideChar = text[i];
              const nextChar = i < text.length - 1 ? text[i + 1] : '\0';

              if (!noWrap && canWrapBetween(lastChar, wideChar, nextChar)) {
                  outWordList.push(wordBuilder);
                  wordBuilder = wideChar;
              } else {
                  wordBuilder += c2;
              }
          } else {
              wordBuilder += c2;
          }
          i++;
      }
      if (wordBuilder.length > 0) {
          outWordList.push(wordBuilder);
      }
      return outWordList;
  }

  const wordList = breakStringIntoWords(txt);

  // --- C# IsUnderwearNeeded Simulation ---
  function isUnderwearNeeded(words, orgWidth, fontSizePx) {
      if (!useUnderwear) return false;
      if (!isFlipped) return true;

      let hasManualNewline = false;
      let totalWidth = 0;
      let bold = false;
      let italic = false;

      ctxTemp.font = `${fontSizePx}px "${font}", sans-serif`;

      for (let t of words) {
          if (t === '[b]') { bold = true; continue; }
          if (t === '[/b]') { bold = false; continue; }
          if (t === '[i]') { italic = true; continue; }
          if (t === '[/i]') { italic = false; continue; }
          if (t === '[d]') { continue; }
          if (t === '\n') {
              hasManualNewline = true;
              break;
          }
          ctxTemp.font = `${italic ? 'italic ' : ''}${fontSizePx}px "${font}", sans-serif`;
          totalWidth += ctxTemp.measureText(t).width;
      }

      return hasManualNewline || totalWidth >= orgWidth;
  }

  function trimLineTrailingSpaces(lineTokens) {
      for (let i = lineTokens.length - 1; i >= 0; i--) {
          const t = lineTokens[i];
          if (t.type === 'bracket') {
              continue; 
          }
          if (t.type === 'text') {
              const trimmed = t.val.trimEnd();
              if (trimmed === '') {
                  lineTokens.splice(i, 1);
              } else {
                  t.val = trimmed;
                  break; 
              }
          }
      }
  }

  // --- Parser Layout Engine ---
   function evaluateLayout(words, currentFSize, knownLineCount = null) {
      // Execute a quick dry-run ONLY for flipped underwear to establish baseline line counts
      if (isFlipped && useUnderwear && knownLineCount === null) {
          const dryRun = evaluateLayout(words, currentFSize, -1);
          knownLineCount = dryRun.lines.length;
      }

      const unityFontSizePx = currentFSize * (charSize * 0.01) * 0.1 * pixelsPerUnit;
      const baseRowHeight = unityFontSizePx * 1.30 * (lineSpacing * lineSpaceModifier);
      const useUnderwearActive = isUnderwearNeeded(words, W, unityFontSizePx);

      const noWrapBeforeCodes = new Set([33, 34, 37, 39, 41, 44, 46, 58, 59, 63, 93, 125, 8217, 8221, 8226]);

      function isUnwrappableToken(tokenText) {
          if (!tokenText) return false;
          for (let i = 0; i < tokenText.length; i++) {
              if (!noWrapBeforeCodes.has(tokenText.charCodeAt(i))) return false;
          }
          return true;
      }

      function getFinalContainerWidth(lineIndex, currentTextWidth) {
          const allowedW = W * resizeToFitWidthModifier;
          if (!useUnderwearActive) return allowedW;

          const allowedNarrowW = narrowWidth * resizeToFitWidthModifier;

          if (isFlipped) {
              // During the flipped dry-run, we return full width to find the baseline line count
              if (knownLineCount === -1) {
                  return allowedW;
              }

              const totalHeightEstimate = knownLineCount * baseRowHeight;
              const textTopY = (H - totalHeightEstimate) * 0.5;
              const lineTopY = textTopY + (lineIndex * baseRowHeight);

              const intersectsY = lineTopY < targetUnderwearHeight;
              const intersectsX = (currentTextWidth * 0.5) > (W * 0.5 - uw);

              if (intersectsY && intersectsX) {
                  return allowedNarrowW;
              }
              return allowedW;
          } else {
              // Non-flipped (bottom cutout) uses the original, sequence-accurate calculation
              const textBottomY = (H + ((lineIndex + 1) * baseRowHeight)) * 0.5;
              const intersectsY = textBottomY > (H - targetUnderwearHeight);
              const intersectsX = (currentTextWidth * 0.5) > (W * 0.5 - uw);

              if (intersectsY && intersectsX) {
                  return allowedNarrowW;
              }
              return allowedW;
          }
      }

      let lines = [];
      let currentLineTokens = [];
      let currentLinePlainText = "";
      let boldState = false;
      let italicState = false;

      for (let i = 0; i < words.length; i++) {
          const token = words[i];

          if (token === '[b]') {
              boldState = true;
              currentLineTokens.push({ type: 'bracket', tag: 'b', boldState, italicState });
              continue;
          } else if (token === '[/b]') {
              boldState = false;
              currentLineTokens.push({ type: 'bracket', tag: '/b', boldState, italicState });
              continue;
          } else if (token === '[i]') {
              italicState = true;
              currentLineTokens.push({ type: 'bracket', tag: 'i', boldState, italicState });
              continue;
          } else if (token === '[/i]') {
              italicState = false;
              currentLineTokens.push({ type: 'bracket', tag: '/i', boldState, italicState });
              continue;
          } else if (token === '[d]') {
              currentLineTokens.push({ type: 'bracket', tag: 'd', boldState, italicState });
              continue;
          } else if (token === '[x]') {
              currentLineTokens.push({ type: 'bracket', tag: 'x', boldState, italicState });
              continue;
          } else if (token === '[/x]') {
              currentLineTokens.push({ type: 'bracket', tag: '/x', boldState, italicState });
              continue;
          } else if (token === '\n') {
              trimLineTrailingSpaces(currentLineTokens);
              const cleanPlainText = currentLineTokens.filter(t => t.type === 'text').map(t => t.val).join('');
              lines.push({ tokens: currentLineTokens, width: ctxTemp.measureText(cleanPlainText).width });

              currentLineTokens = [];
              currentLinePlainText = "";
              continue;
          }

          let tokenText = token;

          ctxTemp.font = `${italicState ? 'italic ' : ''}${unityFontSizePx}px "${font}", sans-serif`;
          const widthWithToken = ctxTemp.measureText(currentLinePlainText + tokenText).width;
          const containerLimit = getFinalContainerWidth(lines.length, widthWithToken);

          const isPunct = isUnwrappableToken(tokenText);
          const lineIsEmpty = currentLinePlainText.length === 0;

          if (widthWithToken < containerLimit || lineIsEmpty || (isPunct && !lineIsEmpty)) {
              currentLineTokens.push({ type: 'text', val: tokenText, boldState, italicState });
              currentLinePlainText += tokenText;
          } else {
              let endsWithDiscretionaryHyphen = false;
              if (currentLineTokens.length > 0) {
                  const lastToken = currentLineTokens[currentLineTokens.length - 1];
                  if (lastToken.type === 'bracket' && lastToken.tag === 'd') {
                      endsWithDiscretionaryHyphen = true;
                  }
              }

              if (endsWithDiscretionaryHyphen) {
                  currentLineTokens.push({ type: 'text', val: '-', boldState, italicState });
                  currentLinePlainText += '-';
              }

              trimLineTrailingSpaces(currentLineTokens);
              const cleanPlainText = currentLineTokens.filter(t => t.type === 'text').map(t => t.val).join('');
              lines.push({ tokens: currentLineTokens, width: ctxTemp.measureText(cleanPlainText).width });

              let wrappedTokenText = tokenText.replace(/^\s+/, '');
              currentLineTokens = [];
              currentLinePlainText = wrappedTokenText;
              if (wrappedTokenText) {
                  currentLineTokens.push({ type: 'text', val: wrappedTokenText, boldState, italicState });
              }
          }
      }

      if (currentLineTokens.length > 0 || lines.length === 0) {
          trimLineTrailingSpaces(currentLineTokens);
          const cleanPlainText = currentLineTokens.filter(t => t.type === 'text').map(t => t.val).join('');
          lines.push({ tokens: currentLineTokens, width: ctxTemp.measureText(cleanPlainText).width });
      }

      const isSingleLine = lines.length === 1;
      const activeLineSpacing = isSingleLine 
          ? (lineSpacing + singleLineAdjustment) 
          : (lineSpacing * lineSpaceModifier);

      const rowHeight = unityFontSizePx * 1.30 * activeLineSpacing;
      let actualTextHeight = isSingleLine 
          ? unityFontSizePx 
          : ((lines.length - 1) * rowHeight + unityFontSizePx);

      let adjustedHeight = actualTextHeight;
      if (!isSingleLine) {
          const intraLineGap = rowHeight - unityFontSizePx;
          adjustedHeight -= intraLineGap;
      }

      const allowedW = W * resizeToFitWidthModifier;

      const heightOverflow = adjustedHeight > H;
      const widthOverflow = lines.some(l => l.width > allowedW);

      return {
          lines: lines,
          totalHeight: actualTextHeight,
          adjustedHeight: adjustedHeight,
          widthLimit: allowedW,
          overflow: heightOverflow || widthOverflow,
          widthOverflow,
          heightOverflow,
          renderedSize: unityFontSizePx
      };
  }

  // --- Dynamic Sizing Pass ---
  const showConsoleDebug = params?.debug !== false;
  if (showConsoleDebug) {
      console.group(`%c[Hearthstone Layout Engine] Processing Card Text`, 'color: #e4a853; font-weight: bold;');
      console.log(`Input String: "${txt}"`);
      console.log(`Initial Font Size: ${initialFontSize}px | Target Bounds: W: ${Math.round(W)}px, H: ${Math.round(H)}px`);
  }

  let activeFontSize = initialFontSize;
  let layoutResult = null;
  let lastFittingLayout = null;
  let lastFittingFontSize = null;
  let passes = 0;
  const maxPasses = 40; 

  while (passes < maxPasses) {
      passes++;
      layoutResult = evaluateLayout(wordList, activeFontSize);

      if (!layoutResult.overflow &&
          (lastFittingFontSize === null || activeFontSize > lastFittingFontSize)) {
          lastFittingLayout = layoutResult;
          lastFittingFontSize = activeFontSize;
      }
      
      if (showConsoleDebug) {
          console.groupCollapsed(`Pass #${passes} (Font Size Estimate: ${activeFontSize.toFixed(2)}px -> Rendered: ${layoutResult.renderedSize.toFixed(1)}px)`);
          console.log(`- Line Count: ${layoutResult.lines.length}`);
          layoutResult.lines.forEach((line, index) => {
              const lineRawText = line.tokens.map(t => {
                  if (t.type === 'text') return t.val;
                  if (t.type === 'bracket') return `[${t.tag}]`;
                  return '';
              }).join('');
              console.log(`  Line ${index + 1}: "${lineRawText}" (Width: ${Math.round(line.width)}px / Limit: ${Math.round(layoutResult.widthLimit)}px)`);
          });
          console.log(`- Layout Status: ${layoutResult.overflow ? '⚠️ OVERFLOW' : '💚 CLEAR'}`);
          console.groupEnd();
      }

      if (resizeEnabled) {
          if (resizeToFitAndGrow) {
              const targetFactor = layoutResult.overflow ? (1.0 - resizeToGrowStep) : (1.0 + resizeToGrowStep);
              const targetSize = activeFontSize * targetFactor;
              
              if (layoutResult.overflow && activeFontSize > minFontSize) {
                  activeFontSize = Math.max(minFontSize, targetSize);
              } else if (!layoutResult.overflow && targetSize <= initialFontSize) {
                  activeFontSize = targetSize;
              } else {
                  break;
              }
          } else {
              if (layoutResult.overflow && activeFontSize > minFontSize) {
                  activeFontSize = Math.max(minFontSize, activeFontSize * (1.0 - resizeShrinkStep));
              } else {
                  break;
              }
          }
      } else {
          break;
      }
  }

  // Growing can step past the fit boundary repeatedly. Render the largest size
  // that was known to fit, rather than whichever side of that boundary ended
  // the pass limit on.
  if (resizeEnabled && resizeToFitAndGrow && lastFittingLayout) {
      layoutResult = lastFittingLayout;
      activeFontSize = lastFittingFontSize;
  }

  if (showConsoleDebug) {
      console.groupEnd();
  }

  // --- Centered Rendering Phase ---
  const activeSizePx = layoutResult.renderedSize;
  const isSingleLine = layoutResult.lines.length === 1;
  const activeLineSpacing = isSingleLine 
      ? (lineSpacing + singleLineAdjustment) 
      : (lineSpacing * lineSpaceModifier);

  const rowHeight = activeSizePx * 1.30 * activeLineSpacing;

  const ox = 0;
  const oy = 0;

  let startY = oy + (H - layoutResult.totalHeight) / 2 + (activeSizePx * 0.78);

  layoutResult.lines.forEach((line, index) => {
      let yPos = startY + (index * rowHeight);
      
      let lineW = 0;
      line.tokens.forEach(t => {
          if (t.type === 'text') {
              const style = t.italicState ? 'italic ' : '';
              ctxTemp.font = `${style}${activeSizePx}px "${font}", sans-serif`;
              lineW += ctxTemp.measureText(t.val).width;
          }
      });

      let xOffset = ox + (W - lineW) / 2;
      let cursorX = xOffset;

      line.tokens.forEach(t => {
          if (t.type === 'bracket') return;

          if (t.type === 'text') {
              const style = t.italicState ? 'italic ' : '';

              ctxTemp.font = `${style}${activeSizePx}px "${font}", sans-serif`;
              ctxTemp.fillStyle = color ? `rgba(${color[0]}, ${color[1]}, ${color[2]}, 1)` : "#1e1710";

              if (t.boldState) {
                  ctxTemp.fillText(t.val, cursorX - 1, yPos - 1);
                  ctxTemp.fillText(t.val, cursorX + 1, yPos - 1);
                  ctxTemp.fillText(t.val, cursorX - 1, yPos + 1);
                  ctxTemp.fillText(t.val, cursorX + 1, yPos + 1);
              }
              ctxTemp.fillText(t.val, cursorX, yPos);

              cursorX += ctxTemp.measureText(t.val).width;
          }
      });
  });

  ctxFinal.drawImage(ctxTemp.canvas, dropX, dropY);

  // --- Debug Visualization Overlay ---
  const showDebug = params?.debug ?? true;
  if (showDebug) {
      ctxFinal.save();

      // 1. Draw Original Boundary
      ctxFinal.strokeStyle = 'rgba(128, 128, 128, 0.5)';
      ctxFinal.lineWidth = 1.5;
      ctxFinal.setLineDash([6, 4]);
      ctxFinal.strokeRect(dropX, dropY, W, originalH);

      // 2. Draw Active Boundary Box
      ctxFinal.strokeStyle = 'rgba(255, 69, 0, 0.8)';
      ctxFinal.lineWidth = 2;
      ctxFinal.setLineDash([]); 
      ctxFinal.strokeRect(dropX, dropY, W, H);

      ctxFinal.fillStyle = 'rgba(255, 69, 0, 1)';
      ctxFinal.font = '11px monospace';
      ctxFinal.fillText(`W: ${Math.round(W)}px | H: ${Math.round(H)}px (Penalty: ${heightPenalty}px)`, dropX + 6, dropY + 16);

      // 3. Draw "Underwear" Cutout Zones
      if (useUnderwear) {
          ctxFinal.fillStyle = 'rgba(128, 0, 128, 0.12)';
          ctxFinal.strokeStyle = 'rgba(128, 0, 128, 0.4)';
          ctxFinal.lineWidth = 1;

          if (isFlipped) {
              ctxFinal.fillRect(dropX, dropY, uw, targetUnderwearHeight);
              ctxFinal.strokeRect(dropX, dropY, uw, targetUnderwearHeight);
              ctxFinal.fillRect(dropX + W - uw, dropY, uw, targetUnderwearHeight);
              ctxFinal.strokeRect(dropX + W - uw, dropY, uw, targetUnderwearHeight);
          } else {
              const uwY = dropY + H - targetUnderwearHeight;
              ctxFinal.fillRect(dropX, uwY, uw, targetUnderwearHeight);
              ctxFinal.strokeRect(dropX, uwY, uw, targetUnderwearHeight);
              ctxFinal.fillRect(dropX + W - uw, uwY, uw, targetUnderwearHeight);
              ctxFinal.strokeRect(dropX + W - uw, uwY, uw, targetUnderwearHeight);
          }
      }

      // 4. Draw Rendered Text Block bounding box
      const textTopY = dropY + (H - layoutResult.totalHeight) / 2;
      ctxFinal.fillStyle = 'rgba(34, 139, 34, 0.07)';
      ctxFinal.strokeStyle = 'rgba(34, 139, 34, 0.6)';
      ctxFinal.lineWidth = 1.5;
      ctxFinal.fillRect(dropX, textTopY, W, layoutResult.totalHeight);
      ctxFinal.strokeRect(dropX, textTopY, W, layoutResult.totalHeight);

      ctxFinal.fillStyle = 'rgba(0, 100, 0, 1)';
      ctxFinal.fillText(`Text Area Height: ${Math.round(layoutResult.totalHeight)}px | FontSize: ${Math.round(activeSizePx)}px`, dropX + 6, textTopY - 6);

      // 5. Draw Individual Line Baselines
      ctxFinal.strokeStyle = 'rgba(0, 191, 255, 0.5)';
      ctxFinal.lineWidth = 1;

      layoutResult.lines.forEach((line, index) => {
          const yPos = dropY + startY + (index * rowHeight);
          
          let lineW = 0;
          line.tokens.forEach(t => {
              if (t.type === 'text') {
                  const style = t.italicState ? 'italic ' : '';
                  ctxTemp.font = `${style}${activeSizePx}px "${font}", sans-serif`;
                  lineW += ctxTemp.measureText(t.val).width;
              }
          });

          const lineLeft = dropX + (W - lineW) / 2;

          ctxFinal.beginPath();
          ctxFinal.moveTo(lineLeft, yPos);
          ctxFinal.lineTo(lineLeft + lineW, yPos);
          ctxFinal.stroke();

          ctxFinal.beginPath();
          ctxFinal.moveTo(lineLeft, yPos - 4);
          ctxFinal.lineTo(lineLeft, yPos + 4);
          ctxFinal.moveTo(lineLeft + lineW, yPos - 4);
          ctxFinal.lineTo(lineLeft + lineW, yPos + 4);
          ctxFinal.stroke();
      });

      ctxFinal.restore();
  }

  return {
    activeFontSize: activeFontSize,
    defaultFontSize: defaultFontSize
  };
};

// --- Font Base64 Generator for SVG Export ---
const fontCache = {};

export const getBase64Font = async (fontUrl, fontFamily) => {
  if (fontCache[fontFamily]) return fontCache[fontFamily];
  try {
    const response = await fetch(fontUrl);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = `@font-face { font-family: '${fontFamily}'; src: url('${reader.result}'); }`;
        fontCache[fontFamily] = result;
        resolve(result);
      };
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn("Failed to fetch font for export", e);
    return "";
  }
};
